import { useState, useEffect, useRef } from 'react';

export interface FetchState<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}
/**
 * A reusable hook for fetching data with automatic re-fetching on dependency updates.
 * 
 * ## How to Use:
 * 1. Import `useFetch` in the components where you want to use it.
 * 2. Import the repository that defines the API endpoint and create an instance of it: const userService = new UserService();
 * 3. Use `useFetch` inside the component to fetch data e.g.:
 *    const { data: permissions = [] } = useFetch(
 *      userService.getPermissions.bind(userService), // Function to fetch data
 *      [],  // Parameters (if any)
 *      []   // Dependencies (when updated, trigger a refetch)
 *    );
 *
 * ## Parameters:
 * - `fetchFunction`: A function that returns a Promise resolving to the expected data.
 * - `params`: Parameters passed to the `fetchFunction`.
 * - `dependencies`: An array of values that, when changed, re-trigger the fetch.
 *
 * ## Notes:
 * - If `dependencies` is `[]`, the fetch runs **only on mount**.
 * - If `params` is an object, wrap it with `useMemo` to avoid unnecessary re-fetches.
 * - Returns `{ data, error, isLoading }`.
 * - Only the latest request may write state: if the dependencies change while a fetch is in
 *   flight (e.g. a quick second date-range pick), the slower, older response is dropped
 *   instead of overwriting the newer one.
 * - On failure `data` is cleared, so a page never shows the previous range's figures under
 *   the new range's heading. Callers should render `error` rather than fall back to zeros.
 */

export const useFetch = <T, P = void>(
  fetchFunction: (params: P) => Promise<T>,
  params: P, // Parameters to be sent with the API call
  dependencies: any[] = [] // This define an array of variables, then when updated, will automatically trigger a re-fetch of data.
): FetchState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const latestRequest = useRef(0);

  const fetchData = async () => {
    const requestId = ++latestRequest.current;
    const isLatest = () => requestId === latestRequest.current;

    // Don't fetch if params is an empty string (for cases where we need a valid ID)
    if (typeof params === 'string' && params.trim() === '') {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await fetchFunction(params);
      if (!isLatest()) return;
      setData(result);
      setError(null);
    } catch (err: any) {
      if (!isLatest()) return;
      setData(null);
      setError(err?.message || 'An error occurred');
    } finally {
      if (isLatest()) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, dependencies);

  const refetch = async () => {
    await fetchData();
  };

  return { data, error, isLoading, refetch };
};
